import { useMemo, useState } from "react";
import type { ImageSourcePropType } from "react-native";
import { Image, ScrollView, Text, View } from "react-native";
import {
  AlertCircle,
  Activity,
  Beaker,
  BookOpen,
  CalendarDays,
  CheckCircle,
  ClipboardCheck,
  DollarSign,
  FileText,
  Heart,
  History,
  Info,
  Moon,
  Plus,
  TestTube2,
  Timer,
  Wrench,
  type LucideIcon,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { CommonActions } from "@react-navigation/native";
import {
  getCollectionLabel,
  getBlocksByKind,
  getDataPointForMarkerName,
  getDataPointsForTest,
  getTestsForDataPoint,
  makeId,
  type ContentBlock,
  type TestBody,
} from "@src/packages/content";
import { useLocale } from "@src/packages/i18n/useLocale";
import { useTranslations } from "@src/packages/i18n";
import {
  getBiomarkerIdentity,
  groupCanonicalResults,
  type CanonicalTestResult,
  useCanonicalTestResults,
} from "@src/packages/test-results";
import {
  getResultTrend,
  toCanonicalTestResult,
} from "@src/packages/test-results/canonical-results";
import {
  useTestResults,
  useDeleteTestResult,
  useUpdateTestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import {
  getBiomarkerFactsheet as getResultBiomarkerFactsheet,
  type BiomarkerFactsheet,
} from "@src/packages/test-results/content/biomarker-factsheets";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import {
  CardSubtitleText,
  CardSurface,
  CloseButton,
  CompactActionButton,
  MetaText,
  SectionTitle,
  SectionTitleRow,
  DisclaimerNote,
  EmptyState,
  MoonSegmentedControl,
  type MoonSegmentedOption,
  LoadingState,
} from "@louise/moon-ui";
import FactSheetHeader, {
  FactSheetBadge,
} from "../../components/FactSheetHeader";
import {
  TEST_QUICK_FACT_LABEL_KEYS,
  dataPointMetaLine,
  hasSampleQuickFact,
  sampleFact,
  whereTestIsDoneFact,
  type TestGlanceFact,
} from "../../components/testGlanceFacts";
import AccessGridCard, {
  AccessGridCardPill,
} from "../../components/access/AccessGridCard";
import EvidenceLevelModal from "../../components/EvidenceLevelModal";
import EvidenceSourceRows from "../../components/EvidenceSourceRows";
import TestResultActionsCard from "../../components/TestResultActionsCard";
import { HistoryChart } from "../../components/HistoryChart";
import {
  DocumentTestResultRow,
  DocumentRow,
  documentTestTubesIcon,
} from "../documents/DocumentRow";
import {
  useResultCategoryResolver,
  type ResultCategoryResolver,
} from "@src/packages/test-results/hooks/useResultCategory";
import type {
  LabResult,
  LabResultHistoryPoint,
} from "../care/sections/ResultsLabsCard";
import TestResultDetailDialog from "../../components/TestResultDetailDialog";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import { getTestIllustration } from "../../components/access/testIllustrations";
import resultHistoryIllustration from "../../../assets/images/document-review-icons/source-documents-3d.png";
import {
  DEV_PREVIEW_AMH_RESULT,
  isDevPreviewLabResultsEnabled,
} from "../../dev/labResultsFixture";
import { semanticColors, withAlpha } from "@louise/theme/tokens";

type Props =
  | NativeStackScreenProps<AccessStackParamList, "AmhComparison">
  | NativeStackScreenProps<AccessStackParamList, "TestComparison">;
type PageTab = "overview" | "values" | "biology" | "testing";
type TestQuickFact = NonNullable<TestBody["quickFacts"]>[number];
type ResultPoint = LabResultHistoryPoint;

const QUICK_FACT_ICONS: Record<TestQuickFact["kind"], LucideIcon> = {
  sample: Beaker,
  time: Timer,
  pain: Activity,
  anesthesia: Moon,
  prep: ClipboardCheck,
  results: FileText,
};

function getTestBlock(id: string): ContentBlock<"test"> | null {
  const candidateIds = id.startsWith("test-") ? [id] : [id, `test-${id}`];
  return (
    getBlocksByKind("test").find((block) =>
      candidateIds.some((candidateId) => block.id === makeId(candidateId)),
    ) ?? null
  );
}

function normalizeUnit(unit: string | null | undefined): string {
  return (unit ?? "").trim().toLowerCase();
}

function buildLabResult(
  results: CanonicalTestResult[],
  factsheet: BiomarkerFactsheet,
  markerName: string,
  formatDate: (date: string) => string,
  categorize: ResultCategoryResolver["categorize"],
): LabResult | null {
  const canonicalHistory = groupCanonicalResults(results)[0];
  if (!canonicalHistory) return null;
  const latest = canonicalHistory.latest;
  // The chart, trend arrow, and detail dialog all read a single top-level
  // unit. A history that mixes units (e.g. 14.28 pmol/L next to 2 ng/mL)
  // would label every point with the latest unit and compute a misleading
  // trend. Only keep points that share the latest unit; unit conversion is a
  // backend responsibility we are not taking on here.
  const latestUnit = normalizeUnit(latest.unit);
  const compatibleResults = canonicalHistory.results.filter(
    (result) => normalizeUnit(result.unit) === latestUnit,
  );

  return {
    id: latest.id,
    dataPointId: canonicalHistory.dataPointId,
    name: latest.name ?? markerName,
    value: latest.value,
    unit: latest.unit ?? "",
    date: latest.date ? formatDate(latest.date) : "",
    cycleId: latest.cycleId,
    trend: getResultTrend(compatibleResults),
    clinicalAssessment: latest.clinicalAssessment,
    category: categorize(latest)?.category ?? null,
    attachment: latest.source.attachment,
    provenance: latest.source,
    isAbnormal:
      latest.clinicalAssessment === "low" ||
      latest.clinicalAssessment === "high",
    // Each past value is placed on its own, not at the latest one's spot.
    history: compatibleResults.map((result) => ({
      id: result.id,
      date: result.date ? formatDate(result.date) : "",
      value: result.value,
      category: categorize(result)?.category ?? null,
      clinicalAssessment: result.clinicalAssessment,
      clinicalAssessmentDetails: result.clinicalAssessmentDetails,
      attachment: result.source.attachment,
      provenance: result.source,
    })),
    clinicalAssessmentDetails: latest.clinicalAssessmentDetails,
    ...factsheet,
  };
}

function InfoBlock({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: string;
}) {
  return (
    <View style={{ gap: 8 }}>
      <SectionTitle icon={Icon}>{title}</SectionTitle>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: semanticColors.text.secondary,
          lineHeight: 21,
        }}
      >
        {children}
      </Text>
    </View>
  );
}

function QuickFacts({
  items,
  extraFacts,
  t,
}: {
  items: NonNullable<TestBody["quickFacts"]>;
  /** Facts the sheet adds after the content's own, such as Where. */
  extraFacts: TestGlanceFact[];
  t: ReturnType<typeof useTranslations>;
}) {
  const facts: TestGlanceFact[] = [
    ...items.map((item, index) => ({
      key: `${item.kind}-${index}`,
      icon: QUICK_FACT_ICONS[item.kind],
      label: item.label ?? t(TEST_QUICK_FACT_LABEL_KEYS[item.kind]),
      value: item.value,
    })),
    ...extraFacts,
  ];
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        columnGap: 10,
        rowGap: 16,
      }}
    >
      {facts.map((fact) => {
        const Icon = fact.icon;
        return (
          <View
            key={fact.key}
            style={{ flexBasis: "47%", flexGrow: 1, minWidth: 140 }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 7,
                marginBottom: 7,
              }}
            >
              <Icon
                size={13}
                color={semanticColors.text.tertiary}
                strokeWidth={1.8}
              />
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 10,
                  fontWeight: "700",
                  color: semanticColors.text.tertiary,
                  textTransform: "uppercase",
                }}
              >
                {fact.label}
              </Text>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: semanticColors.text.secondary,
                lineHeight: 18,
              }}
            >
              {fact.value}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

function MarkerTiming({
  items,
}: {
  items: NonNullable<TestBody["markerTiming"]>;
}) {
  return (
    <View style={{ gap: 14 }}>
      {items.map((item, index) => (
        <View
          key={`${item.marker}-${index}`}
          style={{ flexDirection: "row", gap: 12 }}
        >
          <CalendarDays
            size={17}
            color={semanticColors.text.secondary}
            strokeWidth={1.7}
          />
          <View style={{ flex: 1, gap: 3 }}>
            <SectionTitle level="subsection">{item.marker}</SectionTitle>
            <CardSubtitleText style={{ marginTop: 0 }}>
              {item.timing}
            </CardSubtitleText>
            <MetaText style={{ marginTop: 0 }}>{item.reason}</MetaText>
          </View>
        </View>
      ))}
    </View>
  );
}

function StepList({ items }: { items: string[] }) {
  return (
    <View>
      {items.map((item, index) => (
        <View
          key={`${item.slice(0, 20)}-${index}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingTop: index === 0 ? 0 : 12,
            paddingBottom: index === items.length - 1 ? 0 : 12,
            borderTopWidth: index === 0 ? 0 : 1,
            borderTopColor: semanticColors.border.subtle,
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: withAlpha(semanticColors.text.secondary, 0.1),
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: semanticColors.text.secondary,
              }}
            >
              {index + 1}
            </Text>
          </View>
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              lineHeight: 19,
              color: semanticColors.text.tertiary,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function BenefitList({ items }: { items: string[] }) {
  return (
    <View style={{ gap: 10 }}>
      {items.map((item, index) => (
        <View
          key={`${item.slice(0, 20)}-${index}`}
          style={{ flexDirection: "row", gap: 10 }}
        >
          <CheckCircle
            size={13}
            color={semanticColors.status.success}
            strokeWidth={2}
            style={{ marginTop: 2.5 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              color: semanticColors.text.tertiary,
              lineHeight: 18,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function TrackedDataPoints({
  dataPoints,
  t,
}: {
  dataPoints: ContentBlock<"data-point">[];
  t: ReturnType<typeof useTranslations>;
}) {
  if (dataPoints.length < 2) return null;

  return (
    <View style={{ gap: 10 }}>
      <SectionTitle icon={Beaker}>
        {t("access.testing.comparison.markers_tracked")}
      </SectionTitle>
      {dataPoints.map((dataPoint) => {
        const body = dataPoint.body;
        return (
          <View
            key={dataPoint.id}
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            <Beaker
              size={16}
              color={semanticColors.text.secondary}
              style={{ marginTop: 1 }}
            />
            <View style={{ flex: 1, gap: 6 }}>
              <SectionTitle level="subsection">{dataPoint.title}</SectionTitle>
              {body.interpretation ? (
                <CardSubtitleText numberOfLines={3} style={{ marginTop: 0 }}>
                  {body.interpretation}
                </CardSubtitleText>
              ) : null}
              {body.sampleTypes?.length || body.unit ? (
                <MetaText>
                  {dataPointMetaLine(t, body.sampleTypes, body.unit)}
                </MetaText>
              ) : null}
            </View>
          </View>
        );
      })}
    </View>
  );
}

function CanonicalValues({
  result,
  marker,
  resultSubtitle,
  onOpenDetails,
  onViewAll,
  t,
}: {
  result: LabResult | null;
  marker: string;
  resultSubtitle: string;
  onOpenDetails: (point: ResultPoint) => void;
  onViewAll: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  if (!result) {
    return (
      <View style={{ marginTop: 18 }}>
        <EmptyState
          testID="result-history-empty"
          density="compact"
          illustration={
            <Image
              source={resultHistoryIllustration}
              resizeMode="contain"
              accessible={false}
              testID="result-history-illustration"
              style={{ width: 64, height: 64 }}
            />
          }
          title={t("care.lab_result.no_history_yet")}
          subtitle={t("access.testing.comparison.no_result_connected_yet", {
            params: { marker },
          })}
          action={
            <CompactActionButton
              label={t("access.testing.comparison.add_result")}
              action="access.testing.comparison.add_result"
              variant="secondary"
              onPress={onViewAll}
            />
          }
        />
      </View>
    );
  }

  const valuePoints: ResultPoint[] =
    result.history && result.history.length > 0
      ? result.history
      : [
          {
            id: result.id,
            date: result.date,
            value: result.value,
            category: result.category,
          },
        ];
  const latestFirstPoints = [...valuePoints].reverse();

  return (
    <View>
      <HistoryChart
        history={valuePoints}
        unit={result.unit}
        isAbnormal={result.isAbnormal}
      />

      <View style={{ marginTop: 18 }}>
        <DocumentRow
          title={t("care.lab_result.add_value")}
          subtitle={t("care.lab_result.add_value_body", {
            params: { name: result.name },
          })}
          imageSource={documentTestTubesIcon}
          trailing={
            <Plus
              size={18}
              color={semanticColors.text.tertiary}
              strokeWidth={1.5}
            />
          }
          onPress={onViewAll}
          showChevron={false}
        />
        <View style={{ marginTop: 9, gap: 9 }}>
          {latestFirstPoints.map((point) => (
            <DocumentTestResultRow
              key={point.id}
              title={result.name}
              subtitle={resultSubtitle}
              dateLabel={point.date}
              value={point.value}
              unit={result.unit}
              category={point.category}
              onPress={() => onOpenDetails(point)}
            />
          ))}
        </View>
      </View>
    </View>
  );
}

function OverviewResultState({
  result,
  marker,
  resultSubtitle,
  onOpenResult,
  onUploadResult,
  onGetTested,
  t,
}: {
  result: LabResult | null;
  marker: string;
  resultSubtitle: string;
  onOpenResult: (point: ResultPoint) => void;
  onUploadResult: () => void;
  onGetTested: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  if (result) {
    return (
      <DocumentTestResultRow
        title={result.name}
        subtitle={resultSubtitle}
        dateLabel={result.date}
        value={result.value}
        unit={result.unit}
        category={result.category}
        onPress={() =>
          onOpenResult({
            id: result.id,
            date: result.date,
            value: result.value,
            category: result.category,
            attachment: result.attachment,
          })
        }
      />
    );
  }

  return (
    <TestResultActionsCard
      addResultDescription={t(
        "access.testing.comparison.test_not_logged_description",
        { params: { marker } },
      )}
      getTestedDescription={t(
        "access.testing.comparison.get_tested_description",
      )}
      onAddResult={onUploadResult}
      onGetTested={onGetTested}
      t={t}
    />
  );
}

function TestOptionCard({
  test,
  onPress,
  imageSource,
}: {
  test: ContentBlock<"test">;
  onPress: () => void;
  imageSource: ImageSourcePropType;
}) {
  const body = test.body;
  const collectionLabel = getCollectionLabel(body);
  const cost = body.costRange
    ? `$${body.costRange.low}–$${body.costRange.high}`
    : undefined;

  return (
    <AccessGridCard
      title={test.title}
      description={test.subtitle}
      imageSource={imageSource}
      imageStyle={{ width: 66, height: 84 }}
      onPress={onPress}
      surface="panel"
      tags={
        <>
          <AccessGridCardPill label={collectionLabel} icon={TestTube2} />
          {body.turnaroundTime ? (
            <AccessGridCardPill label={body.turnaroundTime} icon={Timer} />
          ) : null}
          {cost ? <AccessGridCardPill label={cost} icon={DollarSign} /> : null}
        </>
      }
    />
  );
}

export default function AmhComparisonScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { abbrev } = useDateFormat();
  const handleBack = useSmartBack({
    returnTab:
      route.name === "TestComparison" ? route.params.returnTab : undefined,
    fallbackTab: "Access",
    fallbackScreen: "Testing",
  });
  const testResultsQuery = useCanonicalTestResults();
  // Patient-entered values live only in the legacy `test-results` store today
  // (per the open #3084 / #3092 migration — manual entry never reaches
  // `clinical-observations`). Reading them here keeps the factsheet from
  // telling a patient that a value she just typed in doesn't exist. The
  // canonical store still wins when it has the row; the fallback goes dormant
  // automatically once the backend bridges the two.
  const legacyTestResultsQuery = useTestResults();
  const updateTestResult = useUpdateTestResult();
  const deleteTestResult = useDeleteTestResult();
  const devPreviewLabResults = isDevPreviewLabResultsEnabled();
  const [activeTab, setActiveTab] = useState<PageTab>(
    route.params?.initialTab ?? "overview",
  );
  const [selectedValue, setSelectedValue] = useState<ResultPoint | null>(null);
  const [evidenceLevel, setEvidenceLevel] = useState<string | null>(null);

  const comparisonTestId =
    route.name === "AmhComparison" ? "test-amh" : route.params.testId;
  const test = getTestBlock(comparisonTestId);
  const trackedDataPoints = test ? getDataPointsForTest(test.id) : [];
  const primaryDataPoint = trackedDataPoints[0];
  const markerName = (
    primaryDataPoint?.title ??
    test?.body.testName ??
    test?.title ??
    "Test"
  ).replace(/\s+level$/i, "");
  const factsheet = useMemo(
    () => getResultBiomarkerFactsheet(markerName, locale),
    [locale, markerName],
  );
  const linkedTests = useMemo(
    () => (primaryDataPoint ? getTestsForDataPoint(primaryDataPoint.id) : []),
    [primaryDataPoint],
  );
  const markerResults = useMemo(() => {
    const identity = getBiomarkerIdentity(markerName);
    const canonical = (testResultsQuery.data ?? []).filter(
      (result) => getBiomarkerIdentity(result.name) === identity,
    );
    const legacy = (legacyTestResultsQuery.data ?? [])
      .filter((row) => getBiomarkerIdentity(row.name) === identity)
      .map(toCanonicalTestResult);
    // Merge both stores so a typed-in value sits alongside a lab result for
    // the same marker. Dedupe by (date, value, unit) because a lab result
    // uploaded as a document is sometimes also typed in by the patient — the
    // canonical row wins so we keep its lab reference range and provenance.
    const seen = new Set(
      canonical.map(
        (result) =>
          `${result.date ?? ""}|${result.value}|${(result.unit ?? "").trim().toLowerCase()}`,
      ),
    );
    const uniqueLegacy = legacy.filter((result) => {
      const key = `${result.date ?? ""}|${result.value}|${(result.unit ?? "").trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return [...canonical, ...uniqueLegacy];
  }, [markerName, testResultsQuery.data, legacyTestResultsQuery.data]);
  const isAmh = test?.id === makeId("test-amh");
  const { categorize } = useResultCategoryResolver();
  const labResult = useMemo(
    () =>
      isAmh && devPreviewLabResults
        ? DEV_PREVIEW_AMH_RESULT
        : factsheet
          ? buildLabResult(
              markerResults,
              factsheet,
              markerName,
              abbrev,
              categorize,
            )
          : null,
    [
      abbrev,
      categorize,
      devPreviewLabResults,
      factsheet,
      isAmh,
      markerName,
      markerResults,
    ],
  );
  const openResultDialog = (point: ResultPoint) => {
    setSelectedValue(point);
  };
  const handleEditResult = async (
    id: string,
    data: { patientValue?: string; unit?: string; date?: string },
  ) => {
    await updateTestResult.mutateAsync({ testResultId: id, data });
    setSelectedValue(null);
  };
  const handleDeleteResult = async (id: string) => {
    await deleteTestResult.mutateAsync(id);
    setSelectedValue(null);
  };

  if (!test || !factsheet) return null;
  const testBody = test.body;
  const testOptions = [
    test,
    ...linkedTests.filter((linkedTest) => linkedTest.id !== test.id),
  ];
  const includedTests = testOptions.filter(
    (linkedTest) => linkedTest.id !== test.id,
  );
  const resultSubtitle = isAmh
    ? t("all_lab_results.purpose_amh")
    : factsheet.description;
  const biologyText = factsheet.biology;
  const measurementText = factsheet.measurementDetails ?? factsheet.howItWorks;
  const variationText = factsheet.biologicalVariation;
  const howItWorksSteps =
    testBody.howItWorksSteps ??
    testBody.howItWorks
      .split(/(?<=[.!?])\s+/)
      .map((step) => step.trim())
      .filter(Boolean);
  const tabs: MoonSegmentedOption<PageTab>[] = [
    {
      value: "overview",
      label: t("care.lab_result.tab_overview"),
      shortLabel: t("care.lab_result.tab_overview_short"),
      icon: Info,
    },
    {
      value: "values",
      label: t("care.lab_result.tab_values"),
      shortLabel: t("care.lab_result.tab_values_short"),
      icon: History,
    },
    {
      value: "biology",
      label: t("care.lab_result.tab_biology"),
      shortLabel: t("care.lab_result.tab_biology_short"),
      icon: Beaker,
    },
    {
      value: "testing",
      label: t("access.testing.comparison.get_tested"),
      shortLabel: t("access.testing.comparison.get_tested_short"),
      icon: TestTube2,
    },
  ];
  // Where the test is done joins the At a glance grid, and the blood sample
  // only when no quick fact already names it.
  const glanceFacts: TestGlanceFact[] = [
    ...(hasSampleQuickFact(testBody)
      ? []
      : [sampleFact(t, t("access.testing.comparison.blood_test"))]),
    ...(testBody.clinicRequired
      ? [whereTestIsDoneFact(t, "clinic")]
      : testBody.atHome && /lab/i.test(test.subtitle ?? "")
        ? [whereTestIsDoneFact(t, "lab-or-home")]
        : []),
  ];

  const openAllResults = () => {
    navigation.getParent()?.dispatch(
      CommonActions.navigate({
        name: "Care",
        // Name the tab we are leaving so the results screen's back button
        // comes back to this panel rather than dropping the patient on the
        // Care overview, which she was never on.
        params: { screen: "AllLabResults", params: { returnTab: "Access" } },
      }),
    );
  };

  const openRelatedMarker = (marker: string) => {
    const dataPoint = getDataPointForMarkerName(marker);
    if (!dataPoint) return;

    const markerQuery = marker.toLowerCase();
    const linkedTests = getTestsForDataPoint(dataPoint.id);
    const markerTests = linkedTests.filter(
      (linkedTest) => linkedTest.id !== test.id,
    );
    const linkedTest =
      markerTests.find((linkedTest) =>
        [linkedTest.title, linkedTest.body.testName].some((value) =>
          value?.toLowerCase().includes(markerQuery),
        ),
      ) ??
      markerTests.find((linkedTest) =>
        linkedTest.subtitle?.toLowerCase().includes(markerQuery),
      ) ??
      markerTests[0] ??
      linkedTests[0];

    if (linkedTest) {
      const isSingleMarkerTest =
        getDataPointsForTest(linkedTest.id).length === 1;
      navigation.navigate(
        isSingleMarkerTest ? "TestComparison" : "TestDetail",
        { testId: String(linkedTest.id) },
      );
    }
  };

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 560,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <CardSurface
          style={{ paddingHorizontal: 24, paddingTop: 24, paddingBottom: 32 }}
        >
          <View
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 2,
            }}
          >
            <CloseButton
              onPress={handleBack}
              accessibilityLabel={t(
                "learn.article_actions.close_accessibility",
              )}
            />
          </View>

          <FactSheetHeader
            title={markerName}
            subtitle={
              isAmh
                ? t("access.testing.comparison.amh_subtitle")
                : factsheet.description
            }
            imageSource={getTestIllustration({
              testId: String(test.id),
              category: testBody.category,
            })}
            imageSize={86}
          />

          <MoonSegmentedControl
            active={activeTab}
            onChange={setActiveTab}
            tabs={tabs}
          />

          {activeTab === "overview" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <OverviewResultState
                result={labResult}
                marker={markerName}
                resultSubtitle={resultSubtitle}
                onOpenResult={openResultDialog}
                onUploadResult={openAllResults}
                onGetTested={() => setActiveTab("testing")}
                t={t}
              />

              <InfoBlock
                icon={Heart}
                title={t("learn.test_detail.section_what_it_measures")}
              >
                {testBody.whatItMeasures}
              </InfoBlock>

              <View>
                <SectionTitle icon={Info}>
                  {t("access.testing.comparison.at_a_glance")}
                </SectionTitle>
                {testBody.quickFacts?.length || glanceFacts.length ? (
                  <View style={{ marginTop: 10 }}>
                    <QuickFacts
                      items={testBody.quickFacts ?? []}
                      extraFacts={glanceFacts}
                      t={t}
                    />
                  </View>
                ) : null}
              </View>

              {trackedDataPoints.length > 1 ? (
                <TrackedDataPoints dataPoints={trackedDataPoints} t={t} />
              ) : null}

              {testBody.howItWorks ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Wrench}>
                    {t("learn.test_detail.section_how_it_works")}
                  </SectionTitle>
                  <StepList items={howItWorksSteps} />
                </View>
              ) : null}

              {testBody.whyItMatters?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Heart}>
                    {t("learn.test_detail.section_why_it_matters")}
                  </SectionTitle>
                  <BenefitList items={testBody.whyItMatters} />
                </View>
              ) : null}

              <View style={{ gap: 10 }}>
                <SectionTitle icon={Beaker}>
                  {t("care.lab_result.typical_range")}
                </SectionTitle>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    lineHeight: 21,
                    color: semanticColors.text.secondary,
                  }}
                >
                  {factsheet.normalRange}
                </Text>
                {factsheet.normalRangeBullets?.map((bullet) => (
                  <View
                    key={bullet}
                    style={{ flexDirection: "row", gap: 8, paddingLeft: 2 }}
                  >
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 14,
                        color: semanticColors.text.tertiary,
                      }}
                    >
                      •
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        flex: 1,
                        fontSize: 13,
                        lineHeight: 19,
                        color: semanticColors.text.tertiary,
                      }}
                    >
                      {bullet}
                    </Text>
                  </View>
                ))}
              </View>

              {factsheet.relatedMarkers?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Beaker}>
                    {t("access.testing.comparison.connected_markers")}
                  </SectionTitle>
                  <View
                    style={{ flexDirection: "row", flexWrap: "wrap", gap: 7 }}
                  >
                    {factsheet.relatedMarkers.map((marker) => (
                      <FactSheetBadge
                        key={marker}
                        label={marker}
                        onPress={() => openRelatedMarker(marker)}
                      />
                    ))}
                  </View>
                  {includedTests.length ? (
                    <>
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 13,
                          lineHeight: 19,
                          color: semanticColors.text.tertiary,
                        }}
                      >
                        {t("access.testing.comparison.included_in")}
                      </Text>
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: 7,
                        }}
                      >
                        {includedTests.map((includedTest) => (
                          <FactSheetBadge
                            key={includedTest.id}
                            label={includedTest.title}
                            onPress={() =>
                              navigation.navigate("TestDetail", {
                                testId: String(includedTest.id),
                              })
                            }
                          />
                        ))}
                      </View>
                    </>
                  ) : null}
                </View>
              ) : null}

              {test.sources?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={BookOpen}>
                    {t("learn.test_detail.section_evidence")}
                  </SectionTitle>
                  <EvidenceSourceRows
                    sources={test.sources}
                    onEvidenceLevelPress={setEvidenceLevel}
                  />
                </View>
              ) : null}

              {testBody.considerations?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={AlertCircle}>
                    {t("learn.test_detail.section_considerations")}
                  </SectionTitle>
                  {testBody.considerations.map((item) => (
                    <Text
                      key={item}
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        lineHeight: 19,
                        color: semanticColors.text.tertiary,
                      }}
                    >
                      • {item}
                    </Text>
                  ))}
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "values" ? (
            <View style={{ marginTop: 24 }}>
              <SectionTitleRow
                title={t("access.testing.comparison.your_results")}
                icon={History}
                action={{
                  label: t("all_lab_results.title_all"),
                  onPress: openAllResults,
                }}
              />
              {testResultsQuery.isLoading ? (
                <LoadingState style={{ paddingVertical: 16 }} />
              ) : (
                <CanonicalValues
                  result={labResult}
                  marker={markerName}
                  resultSubtitle={resultSubtitle}
                  onOpenDetails={openResultDialog}
                  onViewAll={openAllResults}
                  t={t}
                />
              )}
            </View>
          ) : null}

          {activeTab === "biology" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              {biologyText ? (
                <InfoBlock
                  icon={Beaker}
                  title={t("care.lab_result.biological_basis")}
                >
                  {biologyText}
                </InfoBlock>
              ) : null}
              {measurementText ? (
                <InfoBlock
                  icon={Wrench}
                  title={t("care.lab_result.how_it_is_measured")}
                >
                  {measurementText}
                </InfoBlock>
              ) : null}
              {variationText ? (
                <InfoBlock
                  icon={History}
                  title={t("care.lab_result.how_it_changes_over_time")}
                >
                  {variationText}
                </InfoBlock>
              ) : null}
              {factsheet.testingTiming ? (
                <InfoBlock
                  icon={CalendarDays}
                  title={t("care.lab_result.when_it_is_measured")}
                >
                  {factsheet.testingTiming}
                </InfoBlock>
              ) : null}
              {testBody.markerTiming?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={CalendarDays}>
                    {t("learn.test_detail.section_marker_timing")}
                  </SectionTitle>
                  <MarkerTiming items={testBody.markerTiming} />
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "testing" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
                {testOptions.map((option) => (
                  <View key={option.id} style={{ width: "48%" }}>
                    <TestOptionCard
                      test={option}
                      imageSource={getTestIllustration({
                        testId: String(option.id),
                        category: option.body.category,
                      })}
                      onPress={() =>
                        navigation.navigate("TestDetail", {
                          testId: String(option.id),
                        })
                      }
                    />
                  </View>
                ))}
              </View>
            </View>
          ) : null}

          <DisclaimerNote style={{ marginTop: 32 }}>
            {t("learn.disclaimer_medical_info")}
          </DisclaimerNote>
        </CardSurface>
      </ScrollView>

      <TestResultDetailDialog
        result={labResult}
        point={selectedValue}
        onClose={() => setSelectedValue(null)}
        onAttachSource={async (id, attachmentId) => {
          await updateTestResult.mutateAsync({
            testResultId: id,
            data: { attachmentId },
          });
        }}
        onEdit={handleEditResult}
        onDelete={handleDeleteResult}
      />
      <EvidenceLevelModal
        level={evidenceLevel}
        visible={evidenceLevel !== null}
        onClose={() => setEvidenceLevel(null)}
      />
    </GlassScreenLayout>
  );
}
